import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { useSetClaimDirectionMutation } from '@shared/api';
import type { ClaimDirection } from '@entities/legal/types';
import { ConfirmActions, ConfirmRow, ConfirmText } from './styles';

const Wrap = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const ErrorText = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const TEXTS: Record<ClaimDirection, { text: string; button: string; confirm: string }> = {
  outgoing: {
    text:
      'Похоже, это наш документ: он на бланке АО «САТКОМ» или САТКОМ в нём заявитель. ' +
      'Сейчас он заведён входящим и разобран как требование к нам.',
    button: 'Сделать исходящей и перезапустить анализ',
    confirm:
      'Сделать документ исходящим? Анализ перезапустится как проверка нашего проекта, ' +
      'срок ответа на претензию закроется.',
  },
  incoming: {
    text:
      'Похоже, это требование к нам: САТКОМ в документе — ответчик. ' +
      'Сейчас он заведён исходящим и проверен как наш проект.',
    button: 'Сделать входящей и перезапустить анализ',
    confirm:
      'Сделать документ входящим? Анализ перезапустится как разбор требования к нам, ' +
      'появится срок ответа на претензию.',
  },
};

interface Props {
  claimId: string;
  /** Какое направление подсказывает backend (`direction_hint`). */
  hint: ClaimDirection;
  can_write: boolean;
}

/** Плашка «направление перепутано»: наш документ заведён входящим или наоборот. */
export function ClaimDirectionHint({ claimId, hint, can_write }: Props) {
  const [setDirection, { isLoading }] = useSetClaimDirectionMutation();
  const [error, setError] = useState<string | null>(null);
  const t = TEXTS[hint];

  async function onApply() {
    if (!window.confirm(t.confirm)) return;
    setError(null);
    try {
      await setDirection({ id: claimId, direction: hint }).unwrap();
    } catch (err) {
      setError(
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
          'Не удалось сменить направление. Попробуйте ещё раз.',
      );
    }
  }

  return (
    <Wrap>
      <ConfirmRow>
        <ConfirmText>{t.text}</ConfirmText>
        <ConfirmActions>
          <Button
            type="button"
            onClick={() => void onApply()}
            disabled={isLoading || !can_write}
            title={!can_write ? 'Только чтение' : undefined}
          >
            {isLoading ? 'Меняем…' : t.button}
          </Button>
        </ConfirmActions>
      </ConfirmRow>
      {error && <ErrorText>{error}</ErrorText>}
    </Wrap>
  );
}
